import React, { useEffect, useRef, useState } from 'react';
import Alert from '@amzn/meridian/alert';
import ButtonGroup, { ButtonOption } from '@amzn/meridian/button-group';
import Column from '@amzn/meridian/column';
import Loader from '@amzn/meridian/loader';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import Tile from '@amzn/meridian/tile';
import TrendText from '@amzn/meridian/trend-text';
import { MetricName } from '../dashboard/constants';
import { useAppSelector } from '../../../store';
import { selectStationCode } from '../../../store/slices/shiftPlanSlice';
import { BUFFER_STATE_LABELS, formatFlowNumber, processPathKeyLookup } from '../flowMap/utils';
import { getBuffersPageData } from './api';
import { ProcessPathCard } from '../flowMap/model';
import { TrendWindow } from './constants';
import { GetBuffersPageDataOutput } from './model';
import BufferCard from './BufferCard';
import BufferDetailsPanel, { BUFFER_DETAILS_HEADING_ID } from './BufferDetailsPanel';
import {
    DEFAULT_TREND_WINDOW,
    TREND_WINDOW_OPTIONS,
    actualOf,
    bufferCardLabel,
    bufferMetricOf,
    describeDelta,
    formatCapacityPercent,
    formatDwellDays,
    trendKey,
} from './utils';

/** Width of a summary tile, in CSS pixels. */
const SUMMARY_TILE_WIDTH = 200;

/** One summary tile: a total, what it counts, and its change since last shift. */
const SummaryTile: React.FC<{
    label: string;
    value: number | undefined;
    delta: number | undefined;
    accent: 'theme' | 'success' | 'neutral' | 'error';
    valueColor?: 'error';
    testId: string;
}> = ({ label, value, delta, accent, valueColor, testId }) => {
    const change = describeDelta(delta);
    return (
        <Tile accent={accent} accentPosition="top" data-testid={testId}>
            <Column spacing="100" alignmentHorizontal="center">
                <Text type="d100" color={valueColor} data-testid={`${testId}-value`}>
                    {formatFlowNumber(value)}
                </Text>
                <Text type="h100">{label.toUpperCase()}</Text>
                {change && (
                    <TrendText
                        size="small"
                        arrow={change.arrow}
                        color={change.tone}
                        data-testid={`${testId}-delta`}
                        data-tone={change.tone}
                    >
                        {change.text}
                    </TrendText>
                )}
            </Column>
        </Tile>
    );
};

/**
 * The RCC Buffers page: summary tiles, the most critical buffer, and a card per buffer.
 *
 * Everything shown is the GetBuffersPageData response, rendered as it comes; the page computes
 * no figures of its own. Pressing a card opens its drill-down below the grid — header stats,
 * dwell buckets, trend and the container hierarchy — and pressing it (or the panel's close
 * button) again closes it.
 *
 * Buffer counts are a live snapshot, not a windowed aggregate, so the page has no interval
 * selector; the request leaves `interval` unset.
 */
const BuffersPage: React.FC = () => {
    const stationCode = useAppSelector(selectStationCode);
    const [status, setStatus] = useState<'idle' | 'loading' | 'ready' | 'error'>('idle');
    const [data, setData] = useState<GetBuffersPageDataOutput | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [selectedKey, setSelectedKey] = useState<string | null>(null);
    const [trendWindow, setTrendWindow] = useState<TrendWindow>(DEFAULT_TREND_WINDOW);
    // The window of the trends on screen; the cards label their graph previews with it.
    const [shownWindow, setShownWindow] = useState<TrendWindow>(DEFAULT_TREND_WINDOW);
    // A failed window-change refetch, shown above the page it leaves in place.
    const [refreshError, setRefreshError] = useState<string | null>(null);
    // What is on screen: a refetch for the same warehouse (a window change) keeps the page up.
    const shown = useRef<{ station: string; window: TrendWindow } | null>(null);

    // A new warehouse closes any open drill-down; a new trend window keeps it.
    useEffect(() => {
        setSelectedKey(null);
    }, [stationCode]);

    useEffect(() => {
        if (!stationCode) {
            shown.current = null;
            setRefreshError(null);
            setStatus('idle');
            setData(null);
            setErrorMessage(null);
            return;
        }
        const refetch = shown.current?.station === stationCode;
        // Back on the window already on screen (a failed change reverts to it): nothing to fetch.
        if (refetch && shown.current?.window === trendWindow) return;
        let current = true;
        setRefreshError(null);
        // The first load of a warehouse clears the page and shows the loader; a window change keeps
        // the page up while it refetches, and keeps it if the refetch fails.
        if (!refetch) {
            shown.current = null;
            setData(null);
            setStatus('loading');
        }
        setErrorMessage(null);
        getBuffersPageData({ warehouseId: stationCode, trendWindow })
            .then((output) => {
                if (!current) return;
                shown.current = { station: stationCode, window: trendWindow };
                setShownWindow(trendWindow);
                setData(output);
                setStatus('ready');
            })
            .catch((error: Error) => {
                if (!current) return;
                const message = error?.message || 'Failed to load buffers.';
                if (refetch) {
                    // Keep the page, and put the toggle back on the window it shows, so the labels
                    // match the data and pressing the failed window again retries it.
                    setRefreshError(message);
                    setTrendWindow(shown.current?.window ?? DEFAULT_TREND_WINDOW);
                    return;
                }
                setData(null);
                setErrorMessage(message);
                setStatus('error');
            });
        return () => {
            current = false;
        };
    }, [stationCode, trendWindow]);

    const cards = (data?.bufferCards ?? []).filter((card) => card != null);
    const keyForCard = processPathKeyLookup(cards);
    const trendsByPath = new Map(
        (data?.bufferTrends ?? []).map((entry) => [trendKey(entry.processId, entry.functionId), entry.trend])
    );
    const trendOf = (card: ProcessPathCard) => {
        const path = card.topologyNode?.processPath?.processPath;
        return trendsByPath.get(trendKey(path?.processId, path?.functionId));
    };
    const selectedCard = cards.find((card) => keyForCard(card) === selectedKey);
    const critical = cards.find((card) => bufferCardLabel(card) === data?.mostCriticalPath);
    const criticalBuffer = bufferMetricOf(critical);
    const criticalDwell = critical ? actualOf(critical, MetricName.AVG_DWELL_HOURS) : undefined;

    return (
        <Column spacing="400" spacingInset="500" data-testid="buffers-page">
            {!stationCode && (
                <Alert type="neutral" data-testid="buffers-no-station">
                    Select a warehouse to see its buffers.
                </Alert>
            )}

            {status === 'loading' && (
                <Column alignmentHorizontal="center" spacing="300" spacingInset="500">
                    <Loader size="large" data-testid="buffers-loader" />
                    <Text type="b300" color="secondary">Loading buffers…</Text>
                </Column>
            )}

            {status === 'error' && (
                <Alert type="error" data-testid="buffers-error">
                    {errorMessage}
                </Alert>
            )}

            {status === 'ready' && cards.length === 0 && (
                <Alert type="neutral" data-testid="buffers-empty">
                    No buffers to show for this warehouse yet.
                </Alert>
            )}

            {status === 'ready' && cards.length > 0 && data && (
                <>
                    <Row spacing="400" alignmentHorizontal="center" wrap="down" widths={SUMMARY_TILE_WIDTH}>
                        <SummaryTile
                            label="Total units"
                            value={data.totalUnits}
                            delta={data.totalUnitsDeltaVsLastShift}
                            accent="theme"
                            testId="buffers-tile-units"
                        />
                        <SummaryTile
                            label="Totes"
                            value={data.totalTotes}
                            delta={data.totalTotesDeltaVsLastShift}
                            accent="success"
                            testId="buffers-tile-totes"
                        />
                        <SummaryTile
                            label="Pallets"
                            value={data.totalPallets}
                            delta={data.totalPalletsDeltaVsLastShift}
                            accent="neutral"
                            testId="buffers-tile-pallets"
                        />
                        <SummaryTile
                            label="Units potentially lost"
                            value={data.totalPotentiallyLostUnits}
                            delta={data.totalPotentiallyLostUnitsDeltaVsLastShift}
                            accent="error"
                            valueColor="error"
                            testId="buffers-tile-lost"
                        />
                    </Row>

                    <Row alignmentHorizontal="center">
                        <ButtonGroup
                            value={trendWindow}
                            onChange={(value: string) => setTrendWindow(value as TrendWindow)}
                            size="small"
                            legend="Trend window"
                            data-testid="buffers-trend-window"
                        >
                            {TREND_WINDOW_OPTIONS.map((option) => (
                                <ButtonOption key={option.value} value={option.value}>
                                    {option.label}
                                </ButtonOption>
                            ))}
                        </ButtonGroup>
                    </Row>

                    {refreshError && (
                        <Alert type="warning" size="small" data-testid="buffers-refresh-error">
                            Couldn't update the graph previews for this window: {refreshError}
                        </Alert>
                    )}

                    {data.mostCriticalPath && (
                        <Alert type="warning" data-testid="buffers-most-critical">
                            Most Critical: <strong>{data.mostCriticalPath}</strong>
                            {criticalBuffer && <> — {formatCapacityPercent(criticalBuffer.variancePercentage)} capacity</>}
                            {criticalDwell != null && <> | Avg dwell {formatDwellDays(criticalDwell)}</>}
                            {criticalBuffer?.bufferState && (
                                <>
                                    {' '}
                                    | Buffer{' '}
                                    {(
                                        BUFFER_STATE_LABELS[criticalBuffer.bufferState] ?? criticalBuffer.bufferState
                                    ).toLowerCase()}
                                </>
                            )}
                        </Alert>
                    )}

                    {/* Three cards to a row on Meridian's 12-column grid. */}
                    <Row spacing="300" wrap="down" widths="grid-4" alignmentVertical="stretch" data-testid="buffers-grid">
                        {cards.map((card) => {
                            const cardKey = keyForCard(card);
                            return (
                                <BufferCard
                                    key={cardKey}
                                    card={card}
                                    cardKey={cardKey}
                                    selected={cardKey === selectedKey}
                                    onSelect={() => setSelectedKey(cardKey === selectedKey ? null : cardKey)}
                                    detailsHeadingId={BUFFER_DETAILS_HEADING_ID}
                                    trend={trendOf(card)}
                                    trendWindow={shownWindow}
                                />
                            );
                        })}
                    </Row>

                    {selectedCard && stationCode && (
                        <BufferDetailsPanel
                            key={selectedKey ?? undefined}
                            warehouseId={stationCode}
                            card={selectedCard}
                            onClose={() => setSelectedKey(null)}
                        />
                    )}
                </>
            )}
        </Column>
    );
};

export default BuffersPage;
